import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createUser,
  getUser,
  updateUser,
  type UserRole,
  type TenantUser,
} from '../api/users.js';
import { ApiError } from '../api/apiClient.js';
import { useAuth } from '../auth/useAuth.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

const ALL_ROLES: UserRole[] = ['admin', 'editor', 'agent', 'viewer'];

export interface UserFormProps {
  mode: 'create' | 'edit';
}

export function UserForm({ mode }: UserFormProps): JSX.Element {
  const { id = '', username = '' } = useParams<{ id: string; username?: string }>();
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const queryClient = useQueryClient();
  const { claims } = useAuth();
  const isPlatformOps = (claims?.groups ?? []).includes('platform-ops');

  const userQuery = useQuery({
    queryKey: ['user', id, username],
    queryFn: () => getUser(id, username),
    enabled: mode === 'edit' && Boolean(id && username),
  });

  const initial = useMemo<{
    email: string;
    displayName: string;
    roles: UserRole[];
  }>(() => {
    if (mode === 'edit' && userQuery.data) {
      return {
        email: userQuery.data.email,
        displayName: userQuery.data.displayName ?? '',
        roles: userQuery.data.roles,
      };
    }
    return { email: '', displayName: '', roles: ['admin'] };
  }, [mode, userQuery.data]);

  const [email, setEmail] = useState(initial.email);
  const [displayName, setDisplayName] = useState(initial.displayName);
  const [roles, setRoles] = useState<UserRole[]>(initial.roles);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    setEmail(initial.email);
    setDisplayName(initial.displayName);
    setRoles(initial.roles);
  }, [initial]);

  const createMutation = useMutation({
    mutationFn: () =>
      createUser(id, {
        email,
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
        roles,
      }),
    onSuccess: () => {
      pushToast('success', t.user.created);
      queryClient.invalidateQueries({ queryKey: ['users', id] });
      navigate(`/tenants/${id}/users`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 409) {
        setErrMsg(t.user.emailConflict);
        return;
      }
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
      setErrMsg(msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (patch: { displayName?: string; roles?: UserRole[] }) =>
      updateUser(id, username, patch),
    onSuccess: () => {
      pushToast('success', t.user.updated);
      queryClient.invalidateQueries({ queryKey: ['users', id] });
      queryClient.invalidateQueries({ queryKey: ['user', id, username] });
      navigate(`/tenants/${id}/users`);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
      setErrMsg(msg);
    },
  });

  function toggleRole(r: UserRole): void {
    setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  }

  function onSubmit(ev: React.FormEvent): void {
    ev.preventDefault();
    setErrMsg(null);
    if (mode === 'create') {
      if (!email.trim()) {
        setErrMsg(t.user.emailRequired);
        return;
      }
      if (roles.length === 0) {
        setErrMsg(t.user.rolesRequired);
        return;
      }
      createMutation.mutate();
    } else if (userQuery.data) {
      const patch: { displayName?: string; roles?: UserRole[] } = {};
      const trimmedName = displayName.trim();
      if (trimmedName !== (userQuery.data.displayName ?? '')) patch.displayName = trimmedName;
      const sameRoles =
        roles.length === userQuery.data.roles.length &&
        roles.every((r) => userQuery.data!.roles.includes(r));
      if (!sameRoles) patch.roles = roles;
      if (Object.keys(patch).length === 0) {
        navigate(`/tenants/${id}/users`);
        return;
      }
      updateMutation.mutate(patch);
    }
  }

  if (mode === 'edit' && userQuery.isLoading) return <p>{t.app.loading}</p>;
  if (mode === 'edit' && userQuery.error) {
    return <p className="text-red-700">{(userQuery.error as Error).message}</p>;
  }
  const editing: TenantUser | undefined = mode === 'edit' ? userQuery.data : undefined;
  const submitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      <h2 className="mb-4 text-xl font-semibold">
        {mode === 'create' ? t.user.newTitle : t.user.editTitle}
      </h2>
      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded border border-slate-200 bg-white p-5"
        noValidate
      >
        <Field label={t.user.fields.email} helper={mode === 'edit' ? t.user.emailReadonly : undefined}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={mode === 'edit'}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-100"
            required
          />
        </Field>

        <Field label={t.user.fields.displayName}>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={120}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </Field>

        <Field label={t.user.fields.roles} helper={t.user.rolesHelper}>
          <div className="flex flex-wrap gap-2">
            {ALL_ROLES.map((r) => (
              <label
                key={r}
                className={`flex items-center gap-2 rounded border px-3 py-1 text-sm ${
                  roles.includes(r)
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-slate-300 hover:bg-slate-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={roles.includes(r)}
                  onChange={() => toggleRole(r)}
                />
                <span>{r}</span>
              </label>
            ))}
            {isPlatformOps && (
              <label
                className={`flex items-center gap-2 rounded border px-3 py-1 text-sm ${
                  roles.includes('platform-ops')
                    ? 'border-amber-500 bg-amber-50'
                    : 'border-slate-300 hover:bg-slate-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={roles.includes('platform-ops')}
                  onChange={() => toggleRole('platform-ops')}
                />
                <span>platform-ops</span>
              </label>
            )}
          </div>
        </Field>

        {editing && (
          <div className="rounded bg-slate-50 p-3 text-xs text-slate-600">
            <div>Status: <span className="font-mono">{editing.status ?? '—'}</span></div>
            <div>Sub: <span className="font-mono">{editing.sub}</span></div>
            {editing.createdAt && (
              <div>Creado: {new Date(editing.createdAt).toLocaleString()}</div>
            )}
          </div>
        )}

        {errMsg && (
          <p role="alert" className="text-xs text-red-700">
            {errMsg}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Link
            to={`/tenants/${id}/users`}
            className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
          >
            {t.app.cancel}
          </Link>
          <button
            type="submit"
            disabled={submitting}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {submitting ? '…' : mode === 'create' ? t.user.create : t.app.save}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  helper,
  children,
}: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-700">{label}</span>
      {children}
      {helper && <span className="mt-1 block text-xs text-slate-500">{helper}</span>}
    </label>
  );
}
